import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {getSlaReportData, updateSlaReport} from "../../../../api/evaluation/SlaService";
import {
    DetailFormPageShell,
    DetailListPage,
    DetailPanel,
    DetailSummaryButton,
} from "../../../../components/detailLayout";
import {SlaReportItemRepeat} from "./subPages/SlaReportItemRepeat";
import {formatSlaReportHeroTitle, formatSlaReportMonthLabel} from "./slaReportUtils";
import "../../../../components/detailLayout/detailListLayout.css";
import "./slaReport.css";

function SlaReportUpdate() {
    const {id} = useParams();
    const [sla, setSla] = useState({});
    const [code, setCode] = useState([]);
    const [item, setItem] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setSla({});
        setCode([]);
        setItem([]);

        getSlaReportData(id).then((response) => {
            if (response.status === 201) {
                if (response.data.errorCode === "AUTHORITY") {
                    alert(response.data.reason);
                    navigate(-1);
                }
            }

            if (response.status === 200) {
                setSla(response.data);
                const list = response.data.item ?? [];
                const nextCode = [];
                const nextItem = [];

                list.forEach((each) => {
                    if (each.section === "item") {
                        nextItem.push(each);
                    } else {
                        nextCode.push(each);
                    }
                });

                setCode(nextCode);
                setItem(nextItem);
            }
        });
    }, [id, navigate]);

    const update = async () => {
        const data = {
            ...sla,
            item: [...code, ...item],
        };

        const response = await updateSlaReport(data);
        if (response.status === 200) {
            alert("수정되었습니다.");
            navigate(`/evaluation/slaCompare/report/${id}`, {
                state: {
                    test: location.state,
                },
            });
        }
    };

    const handleInputChange = useCallback((fnc, value, index, target) => {
        fnc((prevData) =>
            prevData.map((dataItem, dataIndex) => {
                if (dataIndex === index) {
                    return {
                        ...dataItem,
                        [target]: value,
                    };
                }
                return dataItem;
            }),
        );
    }, []);

    useEffect(() => {
        if (!code.length && !item.length) {
            return;
        }

        setSla((prevSla) => {
            const codeTotal = code.reduce((acc, curr) => acc + (parseFloat(curr.checkValue) || 0), 0);
            const itemTotal = item.reduce((acc, curr) => acc + (parseFloat(curr.checkValue) || 0), 0);
            const codeCustomerCheckValueTotal = code.reduce(
                (acc, curr) => acc + (parseFloat(curr.customerCheckValue) || 0),
                0,
            );
            const itemCustomerCheckValueTotal = item.reduce(
                (acc, curr) => acc + (parseFloat(curr.customerCheckValue) || 0),
                0,
            );

            return {
                ...prevSla,
                evaluationResult: codeTotal + itemTotal,
                customerEvaluationResult: codeCustomerCheckValueTotal + itemCustomerCheckValueTotal,
            };
        });
    }, [code, item]);

    const heroTitle = useMemo(() => formatSlaReportHeroTitle(sla), [sla]);
    const monthLabel = useMemo(() => formatSlaReportMonthLabel(sla.month), [sla.month]);
    const detailUrl = `/evaluation/slaCompare/report/${id}`;

    if (Object.keys(sla).length === 0) {
        return null;
    }

    return (
        <DetailFormPageShell
            className="common-container container-height detail-list-layout asset-detail-list-layout border-l-gray"
            title={heroTitle}
            meta="평가관리/보고서 > SLA 관리 > SLA 월별보고서 > 수정"
            actions={
                <>
                    <DetailSummaryButton muted onClick={() => navigate(detailUrl)}>
                        취소
                    </DetailSummaryButton>
                    {sla.auth?.update ? (
                        <DetailSummaryButton onClick={update}>저장</DetailSummaryButton>
                    ) : null}
                </>
            }
        >
            <DetailListPage isEditing className="flex min-h-0 flex-1 flex-col">
                <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="asset-detail-list-scroll detail-list-scroll">
                        <DetailPanel title="항목별 평가(월간)">
                            <div className="overflow-x-auto">
                                <table className="w-full create-table table-border" style={{tableLayout: "fixed"}}>
                                    <thead>
                                    <tr>
                                        <th rowSpan={2} className="fieldset-th-sla w-[100px]">서비스 구분</th>
                                        <th rowSpan={2} colSpan={2} className="fieldset-th-sla w-[350px]">측정지표</th>
                                        <th rowSpan={2} className="fieldset-th-sla">가중치</th>
                                        <th rowSpan={2} className="fieldset-th-sla">측정주기</th>
                                        <th colSpan={2} className="fieldset-th-sla">서비스 목표수준</th>
                                        <th colSpan={2} className="fieldset-th-sla">{monthLabel}월 실적(수행사)</th>
                                        <th colSpan={2} className="fieldset-th-sla">{monthLabel}월 실적(고객사)</th>
                                        <th rowSpan={2} className="fieldset-th-sla">비고</th>
                                    </tr>
                                    <tr>
                                        <th className="fieldset-th-sla">목표 수준</th>
                                        <th className="fieldset-th-sla">최소 수준</th>
                                        <th className="fieldset-th-sla">측정 결과</th>
                                        <th className="fieldset-th-sla">평가 점수</th>
                                        <th className="fieldset-th-sla">측정 결과</th>
                                        <th className="fieldset-th-sla">평가 점수</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {code.map((data, index) => (
                                        <tr key={`code-${index}`}>
                                            {index === 0 && (
                                                <>
                                                    <th className="fieldset-th-sla" rowSpan={code.length + 3}>유지보수 서비스</th>
                                                    <th className="fieldset-th-sla" rowSpan={code.length}>가동률</th>
                                                </>
                                            )}
                                            <th className="fieldset-th-sla">{data.section}</th>
                                            <td className="fieldset-td-sla">{data.weight}%</td>
                                            <td className="fieldset-td-sla">월간</td>
                                            <td className="fieldset-td-sla">{data.goal}%</td>
                                            <td className="fieldset-td-sla">{data.min}%</td>
                                            <SlaReportItemRepeat
                                                handleInputChange={handleInputChange}
                                                data={data}
                                                fnc={setCode}
                                                index={index}
                                            />
                                        </tr>
                                    ))}

                                    {item.map((data, index) => (
                                        <React.Fragment key={`item-${index}`}>
                                            {index === 0 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" rowSpan={3}>장애관리</th>
                                                    <th className="fieldset-th-sla">장애 건수</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 1 && (
                                                <tr>
                                                    <th className="fieldset-th-sla">장애 복구율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 2 && (
                                                <tr>
                                                    <th className="fieldset-th-sla">동일 장애발생건수</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 3 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" rowSpan={5}>관리서비스</th>
                                                    <th className="fieldset-th-sla">보고</th>
                                                    <th className="fieldset-th-sla">적기보고 실시율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 4 && (
                                                <tr>
                                                    <th className="fieldset-th-sla">관리</th>
                                                    <th className="fieldset-th-sla">각종 산출물 납기 일정 준수율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 5 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" colSpan={2}>서비스 요청 적기 처리율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 6 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" colSpan={2}>작업 적기 처리율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 7 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" colSpan={2}>점검 적기 처리율</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 8 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" colSpan={3}>성능개선 제안</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                            {index === 9 && (
                                                <tr>
                                                    <th className="fieldset-th-sla" colSpan={3}>내부자료 유출 및 중요 데이터 손실</th>
                                                    <td className="fieldset-td-sla">{data.weight}%</td>
                                                    <td className="fieldset-td-sla">월간</td>
                                                    <td className="fieldset-td-sla">{data.goal}%</td>
                                                    <td className="fieldset-td-sla">{data.min}%</td>
                                                    <SlaReportItemRepeat
                                                        handleInputChange={handleInputChange}
                                                        data={data}
                                                        fnc={setItem}
                                                        index={index}
                                                    />
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    <tr>
                                        <th className="fieldset-th-sla" colSpan={8}>합계</th>
                                        <td className="fieldset-td-sla">{sla.evaluationResult ? `${sla.evaluationResult}%` : "0%"}</td>
                                        <th className="fieldset-th-sla"/>
                                        <td className="fieldset-td-sla">{sla.customerEvaluationResult ? `${sla.customerEvaluationResult}%` : "0%"}</td>
                                        <th className="fieldset-th-sla"/>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </DetailPanel>
                    </div>
                </div>
            </DetailListPage>
        </DetailFormPageShell>
    );
}

export {SlaReportUpdate};
